<template>
  <div
    id="how-to-order"
    class="how-to-order"
  >
    <div class="container">
      <div class="how-to-order__heading">
        <contentful-text
          v-if="!isSpiritJacketContext"
          :item="contentfulText('letter-jacket-how-to-order-heading')"
        />
        <contentful-text
          v-if="isSpiritJacketContext"
          :item="contentfulText('letter-jacket-how-to-order-heading--spirit-jacket')"
        />
      </div>
      <div class="how-to-order__items">
        <div
          :class="step1Classes"
          :style="step1Styles"
          @click="handleStep1Click"
        >
          <div class="how-to-order__item-label">
            <span class="how-to-order__item-label-step">
              <contentful-text :item="contentfulText('letter-jacket-how-to-order-step-text')" />
            </span>
            <contentful-text :item="contentfulText('letter-jacket-how-to-order-step-1-number')" />
          </div>
          <div class="how-to-order__item-heading">
            <contentful-text
              :item="contentfulText('letter-jacket-how-to-order-step-1-text')"
              :apply-markdown="true"
              :render-inline="true"
            />
          </div>
        </div>
        <div
          :class="step2Classes"
          :style="step2Styles"
          @click="handleStep2Click"
        >
          <div class="how-to-order__item-label">
            <span class="how-to-order__item-label-step">
              <contentful-text :item="contentfulText('letter-jacket-how-to-order-step-text')" />
            </span>
            <contentful-text :item="contentfulText('letter-jacket-how-to-order-step-2-number')" />
          </div>
          <div class="how-to-order__item-heading">
            <contentful-text
              :item="contentfulText('letter-jacket-how-to-order-step-2-text')"
              :apply-markdown="true"
              :render-inline="true"
            />
          </div>
        </div>
        <div
          :class="step3Classes"
          :style="step3Styles"
          @click="handleStep3Click"
        >
          <div class="how-to-order__item-label">
            <span class="how-to-order__item-label-step">
              <contentful-text :item="contentfulText('letter-jacket-how-to-order-step-text')" />
            </span>
            <contentful-text :item="contentfulText('letter-jacket-how-to-order-step-3-number')" />
          </div>
          <div class="how-to-order__item-heading">
            <contentful-text
              :item="contentfulText('letter-jacket-how-to-order-step-3-text')"
              :apply-markdown="true"
              :render-inline="true"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GoogleAnalytics from '@Shared/lib/google-analytics'
import ResponsiveBreakpointMixin from '@Shared/mixins/responsive-breakpoints'

const howToOrderItemClasses = {
  base: 'how-to-order__item',
  init: 'how-to-order__item--init',
  active: 'how-to-order__item--active-link',
  inactive: 'how-to-order__item--inactive',
  previous: 'how-to-order__item--previous-link'
}

export default {
  name: 'HowToOrder',
  mixins: [ ResponsiveBreakpointMixin ],
  props: {
    isSpiritJacketContext: { type: Boolean, default: false, required: false }
  },
  data () {
    return {
      howToOrderItemClasses
    }
  },
  computed: {
    model () {
      if (this.$store.state.howToOrderViewModel !== undefined) {
        return this.$store.state.howToOrderViewModel
      } else if (this.$store.state.catalogPageCommon !== undefined) {
        return this.$store.state.catalogPageCommon.howToOrderViewModel
      } else {
        throw Error('Could not find howToOrderViewModel in the store')
      }
    },
    content () {
      if (this.$store.state.howToOrderContent !== undefined) {
        return this.$store.state.howToOrderContent
      } else if (this.$store.state.catalogPageCommon !== undefined) {
        return this.$store.state.catalogPageCommon.howToOrderContent
      } else {
        throw Error('Could not find howToOrderContent in the store')
      }
    },
    step1Classes () {
      return {
        'how-to-order__item--step1': true,
        [this.howToOrderItemClasses.base]: true,
        [this.howToOrderItemClasses.init]: this.model.CurrentStep === 0,
        [this.howToOrderItemClasses.active]: this.model.CurrentStep === 1,
        [this.howToOrderItemClasses.inactive]: false, // never used, here for reference
        [this.howToOrderItemClasses.previous]: this.model.CurrentStep > 1
      }
    },
    step1Styles () {
      return {
        backgroundImage: this.isDesktop ? `url(${this.contentfulImage('letter-jacket-how-to-order-step-1-image').Image.File.Url})` : null
      }
    },
    step2Classes () {
      return {
        'how-to-order__item--step2': true,
        [this.howToOrderItemClasses.base]: true,
        [this.howToOrderItemClasses.init]: this.model.CurrentStep === 0,
        [this.howToOrderItemClasses.active]: this.model.CurrentStep === 2,
        [this.howToOrderItemClasses.inactive]: this.model.CurrentStep > 0 && this.model.CurrentStep < 2,
        [this.howToOrderItemClasses.previous]: this.model.CurrentStep > 2
      }
    },
    step2Styles () {
      return {
        backgroundImage: this.isDesktop ? `url(${this.contentfulImage('letter-jacket-how-to-order-step-2-image').Image.File.Url})` : null
      }
    },
    step3Classes () {
      return {
        'how-to-order__item--step3': true,
        [this.howToOrderItemClasses.base]: true,
        [this.howToOrderItemClasses.init]: this.model.CurrentStep === 0,
        [this.howToOrderItemClasses.active]: this.model.CurrentStep === 3,
        [this.howToOrderItemClasses.inactive]: this.model.CurrentStep > 0 && this.model.CurrentStep < 3,
        [this.howToOrderItemClasses.previous]: false // never used, here for reference
      }
    },
    step3Styles () {
      return {
        backgroundImage: this.isDesktop ? `url(${this.contentfulImage('letter-jacket-how-to-order-step-3-image').Image.File.Url})` : null
      }
    }
  },
  methods: {
    handleStep1Click () {
      if (this.model.CurrentStep !== 1 && this.model.Step1Url !== null) {
        GoogleAnalytics.trackEvent(
          'How to Order Step Link',
          'click',
          this.contentfulText('letter-jacket-how-to-order-step-1-text').Content
        )

        window.location.href = this.model.Step1Url
      }
    },
    handleStep2Click () {
      if (this.model.CurrentStep !== 2 && this.model.Step2Url !== null) {
        GoogleAnalytics.trackEvent(
          'How to Order Step Link',
          'click',
          this.contentfulText('letter-jacket-how-to-order-step-2-text').Content
        )

        window.location.href = this.model.Step2Url
      }
    },
    handleStep3Click () {
      if (this.model.CurrentStep !== 3 && this.model.Step3Url !== null) {
        GoogleAnalytics.trackEvent(
          'How to Order Step Link',
          'click',
          this.contentfulText('letter-jacket-how-to-order-step-3-text').Content
        )

        window.location.href = this.model.Step3Url
      }
    }
  }
}
</script>

<style lang="scss" scoped>
/* overrides styles in the WebResources\scss\shared\modules\how-to-order.scss module */

.how-to-order {
  background-color: #eee;
  border-bottom: transparent;
  border-top: transparent;
  padding-top: 20px;

  &__heading {
    font-weight: normal;
    text-transform: uppercase;
  }

  &__items {
    user-select: none;
  }

  &__item {
    &-label {
      font-weight: normal;
    }

    &-heading {
      font-weight: normal;
    }

    &--step1 {
      background-image: none;
    }

    &--step2 {
      background-image: none;
    }

    &--step3 {
      background-image: none;
    }
  }

  @media screen and (min-width: $screen-md-min) {
    margin-top: 0;

    &__heading {
      padding-right: 40px;
    }

    &__items {
      margin-left: 40px;
      width: 850px;
    }

    &__item {
      background-position: left bottom;
      float: left;
      height: 94px;
      padding-left: 86px;
      padding-top: 0;
      width: 255px;

      &-label {
        font-size: 16px;
        margin: 10px 0 20px 0;

        &::after {
          bottom: -6px;
        }
      }

      &-heading {
        font-size: 22px;
      }

      &--step1 {
        background-size: 75px;
        width: 235px;
      }

      &--step2 {
        background-size: 81px;
        padding-left: 96px;
        width: 275px;
      }

      &--step3 {
        background-size: 75px;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// WebResources/vue/letter-jackets/shared/components/HowToOrder/HowToOrder.vue